import { useEffect, useState } from 'react'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@cutilei/ui'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import AuthService from 'services/auth'
import Phone from 'services/Phone'
import Address from 'services/Address'
import Schedules from './Schedules'
import EditMemberInfoModal from '../EditMemberInfoModal'
import EditMemberAddressModal from '../EditMemberAddressModal'
import AlertDialog from 'components/AlertDialog'
import BlockUserModal from '../BlockUserModal'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import CutileiLogo from 'icons/logo-cutilei-simple.png'
import { format as formatCPF } from '@fnando/cpf'
import { FaEdit } from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  Container,
  Image,
  Row,
  TabRow,
  SubTitle,
  InfoText,
  LinkText,
  Block,
  SmallButton,
  ButtonColumn,
  MoneyText
} from './styles'
import ItemTabs from 'components/ItemTabs'

export default function MemberDetailsModal ({
  visible,
  data: memberData,
  onUpdate: handleUpdate = () => null,
  onClose: handleClose = () => null
}) {
  const OPTION_LIST = [
    {label: 'Dados pessoais', value: 'personal_info'},
    {label: 'Agendamentos', value: 'schedules'}
  ]

  const [member, setMember] = useState (memberData)
  const [option, setOption] = useState (OPTION_LIST[0].value)
  const [disabling, setDisabling] = useState (false)
  const [canceling, setCanceling] = useState (false)
  const [blocking, setBlocking] = useState (false)
  const [showEditInfoModal, setShowEditInfoModal] = useState (false)
  const [showEditAddressModal, setShowEditAddressModal] = useState (false)
  const [showDisableDialog, setShowDisableDialog] = useState (false)
  const [showBlockDialog, setShowBlockDialog] = useState (false)
  const [showUnblockDialog, setShowUnblockDialog] = useState (false)
  const [showCancelDialog, setShowCancelDialog] = useState (false)
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    setMember (memberData)
  }, [memberData])

  const toggleEnableMember = async () => {
    try {
      setDisabling (true)
      let updatedMember

      if (member.enabled) {
        const { data } = await cutileiApi.delete (`/members/${member.id}/disable`, requestConfig)
        updatedMember = data
      } else {
        const { data } = await cutileiApi.put (`/members/${member.id}/enable`, {}, requestConfig)
        updatedMember = data
      }

      setMember (updatedMember)
      handleUpdate (updatedMember)
    } catch (error) {
      logApiError (error)
    } finally {
      setDisabling (false)
    }
  }

  const toggleBlockMember = async (comment = '') => {
    try {
      setBlocking (true)
      let updatedMember

      if (member.enabled) {
        await cutileiApi.put (`/members/${member.id}/block`, {comment}, requestConfig)
        updatedMember = {...member, status: 'blocked', enabled: false}
      } else {
        await cutileiApi.delete (`/members/${member.id}/unblock`, requestConfig)
        updatedMember = {...member, status: 'canceled', enabled: true}
      }

      setMember (updatedMember)
      handleUpdate (updatedMember)
    } catch (error) {
      logApiError (error)
    } finally {
      setBlocking (false)
    }
  }

  const toggleSubscription = async () => {
    try {
      setCanceling (true)

      let updatedMember
      const filters = `member_id=${member.id}`
      const { data: subscription } = await cutileiApi.get (`/subscriptions?${filters}`, requestConfig)

      if (member.status === 'active') {
        const { data } = await cutileiApi.delete (
          `/subscriptions/${subscription.id}?${filters}`, requestConfig
        )
        updatedMember = data
      } else if (member.status === 'canceled') {
        const { data } = await cutileiApi.post (
          `/subscriptions/${subscription.id}/reactivate?${filters}`, {}, requestConfig
        )
        updatedMember = data
      }

      setMember (updatedMember)
      handleUpdate (updatedMember)
    } catch (error) {
      logApiError (error)
    } finally {
      setCanceling (false)
    }
  }

  const toggleEditInfoModal = () => setShowEditInfoModal (!showEditInfoModal)
  const toggleEditAddressModal = () => setShowEditAddressModal (!showEditAddressModal)
  const toggleDisableDialog = () => setShowDisableDialog (!showDisableDialog)
  const toggleBlockDialog = () => setShowBlockDialog (!showBlockDialog)
  const toggleUnblockDialog = () => setShowUnblockDialog (!showUnblockDialog)
  const toggleCancelDialog = () => setShowCancelDialog (!showCancelDialog)

  if (!member) return null

  const { canceled_subscriptions } = member
  const address = Address.formatAddress (member.addresses[0])
  const mapsAddress = Address.formatAddressWithNoComplement (member.addresses[0])
  const addressUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent (mapsAddress)}`

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='2xl' aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{member.name}</DialogTitle>
        </DialogHeader>
        {/* As abas ficam paradas entre o título e o corpo, que rola */}
        <div style={{paddingInline: 24}}>
          <TabRow>
            <ItemTabs
              tabs={OPTION_LIST}
              selectedTab={option}
              onTabChange={setOption}
            />
          </TabRow>
        </div>
        {/* O id é o `scrollableTarget` da rolagem infinita da aba Agendamentos */}
        <DialogBody id='member-details-modal'>
          <Container>
            {option === 'personal_info' && (
              <Block style={{marginBottom: 0}}>
                <Image src={member.profile_picture ?? CutileiLogo}/>
                {member.sponsored && (
                  <MoneyText style={{fontSize: 18, marginBottom: 5}}>
                    Embaixador(a)
                  </MoneyText>
                )}
                <Block>
                  <Row>
                    <SubTitle>E-mail:</SubTitle>
                    <InfoText>{member.email}</InfoText>
                  </Row>
                  <Row>
                    <SubTitle>Contato:</SubTitle>
                    <LinkText
                      href={`https://api.whatsapp.com/send?phone=${member.phone.replace (/\D/g, '')}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{width: 'auto', color: '#44CF6C'}}
                    >
                      <SubTitle>{Phone.formatPhone (member.phone, true)}</SubTitle>
                    </LinkText>
                  </Row>
                  <Row>
                    <SubTitle>CPF:</SubTitle>
                    <InfoText>{formatCPF (member.cpf)}</InfoText>
                  </Row>
                  <Row>
                    <SubTitle>Gênero:</SubTitle>
                    <InfoText>{member.gender}</InfoText>
                  </Row>
                  <Row>
                    <SubTitle>Nascimento:</SubTitle>
                    <InfoText>
                      {DateTime.fromISO (member.birth).toFormat ('dd/MM/yyyy')}
                    </InfoText>
                  </Row>
                  <SmallButton onClick={toggleEditInfoModal}>
                    <FaEdit color='#35A7FF' size={16}/>
                    <InfoText>Editar dados</InfoText>
                  </SmallButton>
                </Block>
                <Block>
                  <SubTitle>Endereço:</SubTitle>
                  <LinkText href={addressUrl} target='_blank' rel='noopener noreferrer'>
                    <SubTitle>{address}</SubTitle>
                  </LinkText>
                  <SmallButton onClick={toggleEditAddressModal}>
                    <FaEdit color='#35A7FF' size={16}/>
                    <InfoText>Editar endereço</InfoText>
                  </SmallButton>
                </Block>
                <Block>
                  <Row>
                    <SubTitle>Cadastro:</SubTitle>
                    <InfoText>
                      {DateTime.fromISO (member.created_at).toFormat ('dd/MM/yyyy - H:mm:ss')}
                    </InfoText>
                  </Row>
                  <Row>
                    <SubTitle>Última alteração:</SubTitle>
                    <InfoText>
                      {DateTime.fromISO (member.updated_at).toFormat ('dd/MM/yyyy - H:mm:ss')}
                    </InfoText>
                  </Row>
                </Block>
                {canceled_subscriptions && (
                  <Block style={{marginBottom: 5}}>
                    <SubTitle style={{color: '#FF3939', marginBottom: 5}}>
                      Cliente cancelado
                    </SubTitle>
                    <Row>
                      <SubTitle>Motivo do cancelamento:</SubTitle>
                      <InfoText>
                        {canceled_subscriptions[0].reason}
                      </InfoText>
                    </Row>
                    {canceled_subscriptions[0].comment && (
                      <Row>
                        <SubTitle>Comentário:</SubTitle>
                        <InfoText>
                          {canceled_subscriptions[0].comment}
                        </InfoText>
                      </Row>
                    )}
                  </Block>
                )}
                <ButtonColumn>
                  <SecondaryButton
                    label={member.enabled ? 'Desativar cliente' : 'Reativar cliente'}
                    onClick={toggleDisableDialog}
                    disabled={disabling}
                    loading={disabling}
                    color={member.enabled ? '#FF3939' : '#252525'}
                    width={160}
                  />
                  <SecondaryButton
                    label={member.status === 'active' ? 'Cancelar assinatura' : 'Reativar a assinatura'}
                    onClick={toggleCancelDialog}
                    disabled={canceling}
                    loading={canceling}
                    color={member.status === 'active' ? '#FF3939' : '#252525'}
                    width={160}
                  />
                  {['canceled', 'blocked'].includes (member.status) && (
                    <SecondaryButton
                      label={member.status === 'canceled' ? 'Bloquear cliente' : 'Desbloquear cliente'}
                      onClick={member.status === 'canceled' ? toggleBlockDialog : toggleUnblockDialog}
                      disabled={blocking}
                      loading={blocking}
                      color={member.status === 'canceled' ? '#FF3939' : '#252525'}
                      width={160}
                    />
                  )}
                </ButtonColumn>
                <EditMemberInfoModal
                  visible={showEditInfoModal}
                  data={member}
                  onConfirm={handleUpdate}
                  onClose={toggleEditInfoModal}
                />
                <EditMemberAddressModal
                  visible={showEditAddressModal}
                  data={member}
                  onConfirm={handleUpdate}
                  onClose={toggleEditAddressModal}
                />
                <AlertDialog
                  visible={showDisableDialog}
                  title='Atenção!'
                  message={`Deseja realmente ${member.enabled ? 'desativar' : 'reativar'} este cliente?`}
                  confirmText='Sim'
                  cancelText='Não'
                  onConfirm={toggleEnableMember}
                  onClose={toggleDisableDialog}
                  dangerous={member.enabled}
                />
                <BlockUserModal
                  visible={showBlockDialog}
                  onConfirm={toggleBlockMember}
                  onClose={toggleBlockDialog}
                />
                <AlertDialog
                  visible={showUnblockDialog}
                  title='Atenção!'
                  message='Deseja realmente desbloquear este cliente?'
                  confirmText='Sim'
                  cancelText='Não'
                  onConfirm={toggleBlockMember}
                  onClose={toggleUnblockDialog}
                />
                <AlertDialog
                  visible={showCancelDialog}
                  title='Atenção!'
                  message={
                    `Deseja realmente ${member.status === 'active' ? 'cancelar' : 'reativar'} a assinatura deste cliente?`
                  }
                  confirmText='Sim'
                  cancelText='Não'
                  onConfirm={toggleSubscription}
                  onClose={toggleCancelDialog}
                  dangerous={member.status === 'active'}
                />
              </Block>
            )}
            {option === 'schedules' && (
              <Schedules member={member}/>
            )}
          </Container>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
